import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Button,
  Popover,
  Paper,
  List,
  ListItem,
  ListItemText,
  Grid,
  Divider,
  Box,
  Typography,
  useTheme as useMuiV4Theme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(relativeTime);

const useStyles = makeStyles((theme) => ({
  button: {
    textTransform: 'none',
    padding: theme.spacing(1, 2),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.paper,
    minWidth: 200,
    height: '100%',
    // Sets a minimum height of 56px to match the standard Material-UI TextField/outlined component height (which is what the SelectAutocomplete components use)
    minHeight: 56,
  },
  popoverPaper: {
    width: 600,
    maxHeight: 500,
  },
  quickOption: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedOption: {
    backgroundColor: theme.palette.action.selected,
  },
  customRangeContainer: {
    padding: theme.spacing(2),
  },
  applyButton: {
    marginTop: theme.spacing(2),
  },
  mainContainer: {
    display: 'flex',
    height: '100%',
  },
  quickOptionsPanel: {
    width: 200,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: 'auto',
  },
  customRangePanel: {
    flex: 1,
    padding: theme.spacing(2),
  },
}));

export interface DateRange {
  from: Dayjs;
  to: Dayjs;
  label: string;
  relativeKey?: string; // If present, this is a relative range that should recalculate on load
}

interface QuickOption {
  label: string;
  key: string;
  getValue: () => DateRange;
}

const createQuickOption = (
  label: string,
  key: string,
  getTimeRange: () => { from: Dayjs; to: Dayjs }
): QuickOption => ({
  label,
  key,
  getValue: () => ({ ...getTimeRange(), label, relativeKey: key }),
});

const quickOptions: QuickOption[] = [
  createQuickOption('Last 15 minutes', 'last_15_minutes', () => ({
    from: dayjs().subtract(15, 'minutes'),
    to: dayjs(),
  })),
  createQuickOption('Last 30 minutes', 'last_30_minutes', () => ({
    from: dayjs().subtract(30, 'minutes'),
    to: dayjs(),
  })),
  createQuickOption('Last 1 hour', 'last_1_hour', () => ({
    from: dayjs().subtract(1, 'hour'),
    to: dayjs(),
  })),
  createQuickOption('Last 3 hours', 'last_3_hours', () => ({
    from: dayjs().subtract(3, 'hours'),
    to: dayjs(),
  })),
  createQuickOption('Last 6 hours', 'last_6_hours', () => ({
    from: dayjs().subtract(6, 'hours'),
    to: dayjs(),
  })),
  createQuickOption('Last 12 hours', 'last_12_hours', () => ({
    from: dayjs().subtract(12, 'hours'),
    to: dayjs(),
  })),
  createQuickOption('Last 24 hours', 'last_24_hours', () => ({
    from: dayjs().subtract(24, 'hours'),
    to: dayjs(),
  })),
  createQuickOption('Last 2 days', 'last_2_days', () => ({
    from: dayjs().subtract(2, 'days'),
    to: dayjs(),
  })),
  createQuickOption('Last 7 days', 'last_7_days', () => ({
    from: dayjs().subtract(7, 'days'),
    to: dayjs(),
  })),
  createQuickOption('Last 30 days', 'last_30_days', () => ({
    from: dayjs().subtract(30, 'days'),
    to: dayjs(),
  })),
  createQuickOption('Last 90 days', 'last_90_days', () => ({
    from: dayjs().subtract(90, 'days'),
    to: dayjs(),
  })),
  createQuickOption('Today', 'today', () => ({
    from: dayjs().startOf('day'),
    to: dayjs(),
  })),
  createQuickOption('Yesterday', 'yesterday', () => ({
    from: dayjs().subtract(1, 'day').startOf('day'),
    to: dayjs().subtract(1, 'day').endOf('day'),
  })),
  createQuickOption('This week', 'this_week', () => ({
    from: dayjs().startOf('week'),
    to: dayjs(),
  })),
  createQuickOption('This month', 'this_month', () => ({
    from: dayjs().startOf('month'),
    to: dayjs(),
  })),
  createQuickOption('All time', 'all_time', () => ({
    from: dayjs().subtract(10, 'years'),
    to: dayjs(),
  })),
];

// Export available quick option labels for convenience
export const QUICK_OPTIONS = quickOptions.map(opt => opt.label);

// Helper to get a date range from a relative key
export function getDateRangeFromRelativeKey(relativeKey: string): DateRange | null {
  const option = quickOptions.find(opt => opt.key === relativeKey);
  return option ? option.getValue() : null;
}

export interface DateRangePickerProps {
  value?: DateRange | null;
  onChange: (range: DateRange | null) => void;
}

export function DateRangePicker(props: DateRangePickerProps): JSX.Element {
  const { value, onChange } = props;
  const classes = useStyles();
  const muiV4Theme = useMuiV4Theme();

  // Create a MUI v5 theme that matches the current theme mode (light/dark)
  // This is needed because @mui/x-date-pickers requires MUI v5 theme system
  const muiV5Theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: muiV4Theme.palette.type === 'dark' ? 'dark' : 'light',
        },
      }),
    [muiV4Theme.palette.type]
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(value?.from || null);
  const [customTo, setCustomTo] = useState<Dayjs | null>(value?.to || null);
  const [selectedQuickOption, setSelectedQuickOption] = useState<string | null>(
    value?.label || null
  );

  const buttonRef = useRef<HTMLButtonElement>(null);

  // Sync internal state with controlled value prop
  useEffect(() => {
    if (value) {
      setCustomFrom(value.from);
      setCustomTo(value.to);
      setSelectedQuickOption(value.label);
    } else {
      // When value is explicitly null, clear the internal state
      setCustomFrom(null);
      setCustomTo(null);
      setSelectedQuickOption(null);
    }
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleQuickOptionClick = (option: QuickOption): void => {
    const range = option.getValue();
    setCustomFrom(range.from);
    setCustomTo(range.to);
    setSelectedQuickOption(option.label);
    onChange(range);
    handleClose();
  };

  const handleApplyCustomRange = (): void => {
    if (customFrom && customTo) {
      const range: DateRange = {
        from: customFrom,
        to: customTo,
        label: 'Custom range',
      };
      setSelectedQuickOption('Custom range');
      onChange(range);
      handleClose();
    }
  };

  const handleClear = (): void => {
    setCustomFrom(null);
    setCustomTo(null);
    setSelectedQuickOption(null);
    onChange(null);
    handleClose();
  };

  const getButtonLabel = (): string => {
    if (!value) {
      return 'Select date range';
    }
    if (selectedQuickOption && selectedQuickOption !== 'Custom range') {
      return selectedQuickOption;
    }
    if (customFrom && customTo) {
      return `${customFrom.format('MMM D, YYYY HH:mm')} - ${customTo.format('MMM D, YYYY HH:mm')}`;
    }
    return 'Select date range';
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      <Button
        ref={buttonRef}
        variant="outlined"
        onClick={handleClick}
        className={classes.button}
        startIcon={<CalendarTodayIcon />}
        fullWidth
      >
        <Typography variant="body2" noWrap>
          {getButtonLabel()}
        </Typography>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          className: classes.popoverPaper,
        }}
      >
        <Paper className={classes.mainContainer}>
          {/* Quick Options Panel */}
          <Box className={classes.quickOptionsPanel}>
            <List dense>
              {quickOptions.map((option) => (
                <ListItem
                  key={option.label}
                  button
                  className={`${classes.quickOption} ${
                    selectedQuickOption === option.label ? classes.selectedOption : ''
                  }`}
                  onClick={() => handleQuickOptionClick(option)}
                >
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Custom Range Panel */}
          <Box className={classes.customRangePanel}>
            <Typography variant="h6" gutterBottom>
              Custom Range
            </Typography>
            <Divider />
            <Box mt={2}>
              <ThemeProvider theme={muiV5Theme}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item>
                      <DateTimePicker
                        label="From"
                        value={customFrom}
                        onChange={(newValue) => setCustomFrom(newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            size: 'small',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <DateTimePicker
                        label="To"
                        value={customTo}
                        onChange={(newValue) => setCustomTo(newValue)}
                        minDateTime={customFrom || undefined}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            size: 'small',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleApplyCustomRange}
                            disabled={!customFrom || !customTo}
                          >
                            Apply
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleClear}
                          >
                            Clear
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </ThemeProvider>
            </Box>
          </Box>
        </Paper>
      </Popover>
    </div>
  );
}

